import { useMemo } from "react"
import { Button } from "@mui/material"
import { PushPin, Check } from "@mui/icons-material"
import Tooltip from "components/material/tooltip"
import { AI_MODULE_LIMIT } from "resources/account"
import { usePinButtonStatus } from "../../hooks/use-pin-button-status"
import type { ArtifactKind } from "connected-components/dashboard/modules/ai-artifact/types"
import { usePinArtifact, type AiTeammateArtifact } from "connected-components/ai-teammate/hooks/use-pin-artifact"
import { useMessagesContext } from "connected-components/ai-teammate/hooks/context"
import { derivePinnedArtifactTitle, type AiTeammateMessage } from "connected-components/ai-teammate/helpers/pinned-artifact-title"

/**
 * Props for PinArtifactButton component
 */
interface PinArtifactButtonProps {
  artifact: AiTeammateArtifact
  kind: ArtifactKind
}

/**
 * Button component for pinning AI artifacts to the dashboard
 *
 * Uses Event Emitter pattern to communicate with Dashboard instead of Context.
 * This ensures AI Teammate components remain decoupled from Dashboard implementation.
 */
export default function PinArtifactButton({ artifact, kind }: PinArtifactButtonProps) {
  const messagesContext = useMessagesContext()
  const { isLimitReached, isAlreadyOnDashboard } = usePinButtonStatus(artifact.id)

  const title = useMemo(() => derivePinnedArtifactTitle({
    artifact,
    kind,
    messages: (messagesContext?.messages ?? []) as AiTeammateMessage[],
  }),
  [artifact, kind, messagesContext?.messages])

  const { pin, isPinning, isPinned: isPinnedInCurrentSession } = usePinArtifact({ artifact, kind, title })
  // Local click-feedback vs dashboard-wide persisted status.
  const isPinnedOrOnDashboard = isPinnedInCurrentSession || isAlreadyOnDashboard

  let buttonText = "Pin to Dashboard"
  if (isPinnedOrOnDashboard) {
    buttonText = "Pinned!"
  } else if (isPinning) {
    buttonText = "Pinning..."
  }

  const isDisabled = isPinning || isPinnedOrOnDashboard || isLimitReached

  return (
    <Tooltip
      title={isLimitReached && !isPinnedOrOnDashboard ? `You've reached your limit of ${AI_MODULE_LIMIT} modules` : ""}
      placement="top"
    >
      <span>
        <Button
          size="small"
          variant="outlined"
          color="primary"
          startIcon={isPinnedOrOnDashboard ? <Check /> : <PushPin />}
          onClick={pin}
          disabled={isDisabled}
        >
          {buttonText}
        </Button>
      </span>
    </Tooltip>
  )
}
